import type { PermissionModule } from '@/lib/types';
import type { 
  Permission, 
  PermissionContext, 
  PermissionOperator,
  PermissionCheckResult
} from '@/lib/types/permissions';
import { roleManager } from './roleManager';
import { getAuth } from '@/lib/firebase/core';
import { serviceLogger } from '@/lib/utils/logger';

const logger = serviceLogger('PermissionManager');

/**
 * Simplified Permission Manager
 * Combines functionality from permissionService and enhancedPermissionService
 * 
 * Features:
 * - Role-based access control
 * - Granular permission checking
 * - Context-aware permissions
 * - Super Admin bypass
 * - Permission module management
 */
export class PermissionManager {
  private static instance: PermissionManager;
  
  private constructor() {}
  
  public static getInstance(): PermissionManager {
    if (!PermissionManager.instance) {
      PermissionManager.instance = new PermissionManager();
    }
    return PermissionManager.instance;
  }

  /**
   * Get organized permission modules from roleManager
   */
  async getPermissionModules(): Promise<PermissionModule[]> {
    try {
      const permissions = roleManager.getAvailablePermissions();
      const modules = new Map<string, Set<string>>();
      
      permissions.forEach(permission => {
        const [resource] = permission.split('.');
        const moduleKey = this.getModuleForResource(resource);
        if (!modules.has(moduleKey)) {
          modules.set(moduleKey, new Set());
        }
        modules.get(moduleKey)!.add(permission);
      });

      return Array.from(modules.entries()).map(([module, perms]) => ({
        id: module,
        name: module.replace(/_/g, ' ').split(' ').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' '),
        category: 'CORE' as const,
        permissions: Array.from(perms).map(permissionStr => {
          const [resource, action] = permissionStr.split('.');
          return {
            id: `${resource}_${action}`,
            module,
            resource,
            action,
            scope: 'ORGANIZATION' as const,
            description: `Permission to ${action} ${resource}`
          };
        }),
        description: `Module for ${module} related permissions`
      }));
    } catch (error) {
      logger.error('Error getting permission modules:', error);
      return [];
    }
  }

  /**
   * Get permissions for a specific role
   */
  async getRolePermissions(roleId: string): Promise<string[]> {
    try {
      const roles = await roleManager.getRoles();
      const role = roles.find((r: { name: string; permissions?: string[] }) => r.name === roleId);
      if (!role) {
        throw new Error('Role not found');
      }
      return role.permissions || [];
    } catch (error) {
      logger.error('Error getting role permissions:', error);
      throw error;
    }
  }

  /**
   * Update permissions for a specific role
   */
  async updateRolePermissions(roleId: string, permissions: string[]): Promise<void> {
    try {
      const success = await roleManager.updateRolePermissions(roleId, permissions);
      if (!success) {
        throw new Error('Failed to update role permissions');
      }
    } catch (error) {
      logger.error('Error updating role permissions:', error);
      throw error;
    }
  }

  /**
   * Check if user has a specific permission
   * SUPER ADMIN ALWAYS RETURNS TRUE
   */
  async hasPermission(permission: Permission, context?: PermissionContext): Promise<boolean> {
    try {
      const authInstance = getAuth();
      if (!authInstance?.currentUser) return false;

      const idTokenResult = await authInstance.currentUser.getIdTokenResult();
      const roles = idTokenResult.claims.roles as string[] || ['Member'];
      
      // SUPER ADMIN BYPASS: Always grant access to Super Admin
      if (roles.includes('Super Admin')) {
        logger.debug(`Super Admin bypass for permission: ${permission}`);
        return true;
      }

      // Check role-based permissions for non-Super Admin users
      return this.checkRolePermissions(roles, permission, context);
    } catch (error) {
      logger.error('Error checking permission:', error);
      return false;
    }
  }

  /**
   * Check multiple permissions with AND/OR operator
   * SUPER ADMIN ALWAYS RETURNS GRANTED
   */
  async hasPermissions(
    permissions: Permission[], 
    operator: PermissionOperator = 'AND',
    context?: PermissionContext
  ): Promise<PermissionCheckResult> {
    try {
      const authInstance = getAuth();
      if (!authInstance?.currentUser) {
        return { granted: false, reason: 'Not authenticated' };
      }

      const idTokenResult = await authInstance.currentUser.getIdTokenResult();
      const roles = idTokenResult.claims.roles as string[] || ['Member'];
      
      // SUPER ADMIN BYPASS: Always grant access
      if (roles.includes('Super Admin')) {
        logger.debug(`Super Admin bypass for permissions: ${permissions.join(', ')}`);
        return { granted: true };
      }

      if (permissions.length === 0) {
        return { granted: true };
      }

      const results = await Promise.all(
        permissions.map(permission => this.hasPermission(permission, context))
      );

      const granted = operator === 'AND' 
        ? results.every(result => result)
        : results.some(result => result);

      return {
        granted,
        reason: granted ? undefined : `Missing required permission(s): ${permissions.join(', ')}`
      };
    } catch (error) {
      logger.error('Error checking permissions:', error);
      return { granted: false, reason: 'Permission check failed' };
    }
  }

  /**
   * Check if user has specific role(s)
   * SUPER ADMIN ALWAYS RETURNS TRUE
   */
  async hasRole(roles: string | string[], operator: PermissionOperator = 'OR'): Promise<boolean> {
    try {
      const authInstance = getAuth();
      if (!authInstance?.currentUser) return false;

      const idTokenResult = await authInstance.currentUser.getIdTokenResult();
      const userRoles = idTokenResult.claims.roles as string[] || ['Member'];
      
      // SUPER ADMIN BYPASS: Always grant access
      if (userRoles.includes('Super Admin')) {
        logger.debug(`Super Admin bypass for role check: ${Array.isArray(roles) ? roles.join(', ') : roles}`);
        return true;
      }

      const rolesToCheck = Array.isArray(roles) ? roles : [roles];
      
      if (operator === 'AND') {
        return rolesToCheck.every(role => userRoles.includes(role));
      } else {
        return rolesToCheck.some(role => userRoles.includes(role));
      }
    } catch (error) {
      logger.error('Error checking roles:', error);
      return false;
    }
  }

  /**
   * Check if user is Super Admin
   */
  async isSuperAdmin(): Promise<boolean> {
    try {
      const authInstance = getAuth();
      const idTokenResult = await authInstance?.currentUser?.getIdTokenResult();
      if (!idTokenResult) return false;

      const roles = idTokenResult.claims.roles as string[] || ['Member'];
      return roles.includes('Super Admin');
    } catch (error) {
      logger.error('Error checking Super Admin status:', error);
      return false;
    }
  }

  /**
   * Get access denied message
   */
  getAccessDeniedMessage(
    action: string, 
    resource: string,
    context?: PermissionContext
  ): string {
    const authInstance = getAuth();
    
    if (!authInstance?.currentUser) {
      return 'Please log in to access this resource.';
    }

    const contextMessage = context?.clubId 
      ? ` in club ${context.clubId}`
      : '';

    return `You don't have permission to ${action} ${resource}${contextMessage}.`;
  }

  /**
   * Check role-based permissions
   */
  private checkRolePermissions(
    roles: string[],
    permission: Permission,
    _context?: PermissionContext
  ): boolean {
    // Check if user has required role for permission
    switch (permission) {
      case 'manage_spaces':
        return roles.includes('Admin') || roles.includes('Manager');
      case 'view_spaces':
        return roles.includes('Admin') || roles.includes('Manager') || roles.includes('Member');
      case 'manage_clubs':
        return roles.includes('Admin');
      case 'view_clubs':
        return roles.includes('Admin') || roles.includes('Manager') || roles.includes('Member');
      case 'manage_users':
        return roles.includes('Admin');
      case 'view_users':
        return roles.includes('Admin') || roles.includes('Manager');
      default:
        return false;
    }
  }

  /**
   * Map resources to modules
   */
  private getModuleForResource(resource: string): string {
    const resourceModuleMap: Record<string, string> = {
      'users': 'user_management',
      'spaces': 'space_management',
      'bookings': 'booking_management',
      'analytics': 'analytics',
      'platform': 'platform_admin',
      'clubs': 'club_management',
      'organizations': 'organization_management'
    };
    return resourceModuleMap[resource] || 'core';
  }
}

// Export singleton instance
export const permissionManager = PermissionManager.getInstance(); 
